import { ActivatedRoute, Data } from '@angular/router';
import { ApiBlockSettings, ApiSettings, ApiSlideSettings } from '../../../../models/settings.model';
import { Component, OnInit } from '@angular/core';

import { AppConfig } from './../../../../app.config';
import { BlockPages } from './../../../../models/pages.enum';
import { ContentService } from 'apps/arsultima/src/app/services/content.service';
import { DataService } from 'apps/arsultima/src/app/services/data.service';
import { HOME_MAX_BLOCKS } from './../../home.config';
import { HomeCard } from '../../models/home.model';
import { IMAGES_FILENAME_PREFIX } from './../../../../models/image.model';
import { ImageService } from './../../../../services/image.service';
import { NavigateService } from './../../../../core/authentication/services/navigate/navigate.service';
import { SiteType } from './../../../../models/auth.model';
import { TranslocoService } from '@ngneat/transloco';
import { URL_ROUTER } from 'apps/arsultima/src/app/utils/url-router';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'arsultima-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
  isConnected: boolean;

  currentDb: string;
  connectedUserDb: string;
  siteType: SiteType;

  id: string;
  imageUrl: string;

  blocks: ApiBlockSettings[];
  homeCards: HomeCard[];
  slides: ApiSlideSettings[];

  discoverSlide: ApiSlideSettings;

  routerData: Data;
  page = BlockPages.HOME;

  constructor(
    private readonly dataService: DataService,
    private readonly contentService: ContentService,
    private readonly navigateService: NavigateService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly imageService: ImageService,
    private translocoService: TranslocoService,
  ) {
    this.setBlockPages();
  }

  ngOnInit(): void {
    this.getConnection();
    this.setHomeCards();
    this.getHomeData();
  }

  get isEditable(): boolean {
    return this.isCollectionDbMatchingConnectedClient();
  }

  get path(): { [key: string]: string } {
    const db = this.currentDb;
    const connected = this.isConnected;
    const links = {
      exhibit: URL_ROUTER.exhibitWall(db),
      contact: !connected ? URL_ROUTER.contactArsultima() : URL_ROUTER.contactCollection(db),
      home: connected ? URL_ROUTER.homeArsultima() : URL_ROUTER.homeCollection(db),
      discover: URL_ROUTER.discoverArsultima(),
    };
    return links;
  }

  get notMoreThanMaxBlock(): boolean {
    return this.blocks?.length < HOME_MAX_BLOCKS;
  }

  get isDiscover() {
    return this.page === BlockPages.DISCOVER;
  }

  isDefaultDb(): boolean {
    return this.currentDb === AppConfig.DEFAULT_DB;
  }

  isArsultimaHomePage(): boolean {
    return this.siteType === SiteType.ARSU && !this.isDiscover;
  }

  isClientHomePage() {
    return this.isClientHome();
  }

  isCollectionDbMatchingConnectedClient(): boolean {
    return this.currentDb === this.connectedUserDb;
  }

  isClientHome(): boolean {
    return this.siteType === SiteType.CLIENT;
  }

  isArsultimaHome(): boolean {
    return this.siteType === SiteType.ARSU;
  }

  addBlock() {
    const currentLastBlockID = this.blocks[this.blocks.length - 1]?.rank || 0;
    this.navigateService.navigate(URL_ROUTER.blockAdd(), {
      queryParams: { page: this.page, currentRank: currentLastBlockID },
    });
  }

  private setBlockPages(): void {
    this.activatedRoute.data.subscribe(data => {
      const { blockPage } = data;
      if (blockPage) {
        this.page = blockPage;
      }
    });
  }

  private getConnection() {
    this.dataService.currentUserConnection$.subscribe(connection => {
      const { db, site, connectedUserDb } = connection;
      this.siteType = site;
      this.currentDb = db;
      this.connectedUserDb = connectedUserDb;
    });
  }

  private setHomeCards(): void {
    const translate = this.translocoService.translate('menu.home');
    this.homeCards = [
      {
        title: translate.discover,
        icon: 'library_books',
        path: this.path.discover,
      },
      {
        title: translate.exhibit,
        icon: 'collections',
        path: this.path.exhibit,
      },
      {
        title: translate.contact,
        icon: 'mail',
        path: this.path.contact,
      },
    ];
  }

  private getHomeData(): void {
    this.contentService.getSettings().subscribe(settings => {
      this.setSlidersData(settings);
      this.setBlocksData(settings);
    });
  }

  private setSlidersData(settings: ApiSettings) {
    if (settings[this.page] && settings[this.page].slides) {
      if (this.page === BlockPages.DISCOVER) {
        this.discoverSlide = settings[this.page].slides[0];
        this.imageUrl = this.imageService.getImageUrl(this.discoverSlide.ID, {
          prefix: IMAGES_FILENAME_PREFIX.slides,
        });
      }
      this.slides = settings.home.slides.sort((a, b) => a.rank - b.rank);
    }
  }

  private setBlocksData(settings: ApiSettings) {
    if (settings[this.page] && settings[this.page].blocks) {
      const blocks = settings[this.page].blocks;
      this.blocks = blocks.sort((a: ApiBlockSettings, b: ApiBlockSettings) => {
        return a.rank - b.rank;
      });
    }
  }
}
